import React, { useEffect, useMemo, useState } from "react";
import { Table, notification } from "antd";
import type { ColumnsType } from "antd/es/table";
import Button from "../../../commons/Button/Button";
import Spinner from "../../../commons/Spinner/Spinner";
import useUsersWithFcmToken from "../../../state/hooks/notifications-management/useUsersWithFcmToken";
import { IUserWithFcmToken } from "../../../models/Interface/NotificationManagement.interface";
import { MAX_RECIPIENTS, RECIPIENT_PICKER_PAGE_SIZE } from "../constants";
import { getApiErrorMessage } from "../helpers";
import {
  CountBadge,
  EllipsisCell,
  NotificationTableStyles,
} from "./NotificationCells";

const TABLE_SCOPE = "notification-recipients-table";

interface Props {
  selectedEmails: string[];
  onAdd: (email: string) => void;
}

const RecipientPicker = ({ selectedEmails, onAdd }: Props) => {
  const [search, setSearch] = useState("");
  /** The term the list is actually filtered by — only a Search/Clear press moves it. */
  const [appliedSearch, setAppliedSearch] = useState("");
  const [page, setPage] = useState(1);

  useEffect(() => {
    setPage(1);
  }, [appliedSearch]);

  const { data, isLoading, isFetching, error } = useUsersWithFcmToken({
    page,
    limit: RECIPIENT_PICKER_PAGE_SIZE,
    email: appliedSearch || undefined,
  });

  const handleSearch = () => setAppliedSearch(search.trim());

  const handleClear = () => {
    setSearch("");
    setAppliedSearch("");
  };

  const users = data?.users ?? [];
  const total = data?.total ?? 0;

  const selectedSet = useMemo(
    () => new Set(selectedEmails.map((email) => email.toLowerCase())),
    [selectedEmails]
  );
  const isCapReached = selectedEmails.length >= MAX_RECIPIENTS;

  useEffect(() => {
    if (!error) return;
    notification.error({
      key: "users-with-fcm-token-load-error",
      message: "Error",
      description: getApiErrorMessage(
        error,
        "Failed to load users with an FCM token."
      ),
      placement: "top",
    });
  }, [error]);

  const isSelected = (user: IUserWithFcmToken) =>
    selectedSet.has(user.email.toLowerCase());

  const columns: ColumnsType<IUserWithFcmToken> = [
    {
      title: "Email",
      dataIndex: "email",
      key: "email",
      ellipsis: true,
      render: (email: string) => <EllipsisCell value={email} strong />,
    },
    {
      title: "Name",
      dataIndex: "firstName",
      key: "firstName",
      width: 180,
      ellipsis: true,
      render: (firstName: string) => <EllipsisCell value={firstName} />,
    },
    {
      title: "Language",
      dataIndex: "interfaceLanguage",
      key: "interfaceLanguage",
      width: 130,
      render: (language: string) => <CountBadge>{language || "—"}</CountBadge>,
    },
    {
      title: "Plan",
      dataIndex: "premium",
      key: "premium",
      width: 130,
      render: (premium: boolean) => (
        <CountBadge tone={premium ? "accent" : "neutral"}>
          {premium ? "Premium" : "Free"}
        </CountBadge>
      ),
    },
    {
      title: "",
      key: "actions",
      width: 130,
      fixed: "right",
      align: "right",
      render: (_: unknown, user: IUserWithFcmToken) =>
        isSelected(user) ? (
          <span
            className="inline-flex items-center gap-1 text-xs font-semibold whitespace-nowrap"
            style={{ color: "#04AA0A" }}
          >
            ✓ Added
          </span>
        ) : (
          <div className="flex justify-end">
            <Button
              type="secondary"
              border
              height="20"
              paddingHorizontal="10"
              fontSize="sm"
              disabled={isCapReached}
              onClick={() => onAdd(user.email)}
            >
              Add
            </Button>
          </div>
        ),
    },
  ];

  return (
    <div className="bg-white rounded-xl border border-light-grey-3 border-solid p-20 sm:p-30 w-full min-w-0 overflow-hidden">
      <div className="flex items-start justify-between mb-20 gap-20 flex-wrap">
        <div className="flex flex-col gap-10 min-w-0">
          <div className="flex items-center gap-10">
            <h3 className="text-dark-grey font-bold text-lg m-0">
              Reachable Recipients
            </h3>
            {isFetching && !isLoading && <Spinner />}
          </div>
          <div className="flex items-center gap-10 flex-wrap">
            <CountBadge>{total.toLocaleString()} reachable</CountBadge>
            <CountBadge
              tone={selectedEmails.length > 0 ? "accent" : "neutral"}
            >
              {selectedEmails.length}/{MAX_RECIPIENTS} selected
            </CountBadge>
          </div>
        </div>
        <div className="flex items-center gap-10 flex-wrap">
          <input
            type="text"
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            onKeyDown={(e) => {
              if (e.key === "Enter") handleSearch();
            }}
            placeholder="Search by exact email..."
            className="rounded-md border border-light-grey-3 border-solid px-4 py-3 text-sm text-dark-grey
              focus:outline-none focus:ring-2 focus:ring-primary focus:border-transparent"
            style={{ minWidth: 320 }}
            id="notification-recipient-search"
          />
          <Button
            type="secondary"
            border
            paddingHorizontal="20"
            onClick={handleSearch}
            disabled={search.trim() === appliedSearch}
          >
            Search
          </Button>
          {appliedSearch && (
            <Button
              type="secondary"
              border
              paddingHorizontal="20"
              onClick={handleClear}
            >
              Clear
            </Button>
          )}
        </div>
      </div>

      <p className="text-sm text-dark-grey mb-20" style={{ opacity: 0.55 }}>
        Everyone listed here holds an FCM token, so they can receive a push. Add
        drops the address into the send form above. Search matches the full
        email exactly — partial addresses return nothing.
      </p>

      {isCapReached && (
        <p className="text-xs text-error mb-10">
          Recipient limit of {MAX_RECIPIENTS} reached — remove an address above
          to add another.
        </p>
      )}

      <div className={`w-full min-w-0 ${TABLE_SCOPE}`}>
        <Table<IUserWithFcmToken>
          dataSource={users}
          columns={columns}
          rowKey="subUserId"
          loading={isLoading}
          size="middle"
          tableLayout="fixed"
          scroll={{ x: 900 }}
          rowClassName={(user) =>
            isSelected(user) ? "notification-recipient-selected" : ""
          }
          pagination={{
            current: page,
            pageSize: RECIPIENT_PICKER_PAGE_SIZE,
            total,
            showSizeChanger: false,
            onChange: setPage,
            showTotal: (count, range) =>
              `${range[0]}–${range[1]} of ${count.toLocaleString()}`,
          }}
          locale={{
            emptyText: appliedSearch
              ? "No reachable user with this exact email."
              : "No users with an FCM token found.",
          }}
        />
        <NotificationTableStyles scope={TABLE_SCOPE} />
        <style>{`
          .${TABLE_SCOPE} .ant-table-tbody > tr.notification-recipient-selected > td {
            background: #F4F5FE !important;
          }
          .${TABLE_SCOPE} .ant-table-tbody > tr.notification-recipient-selected:hover > td {
            background: #EDEFFB !important;
          }
        `}</style>
      </div>
    </div>
  );
};

export default RecipientPicker;
